import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector, useStore } from 'react-redux';

import {
  SIDEKICK_FEATURE_EVENT,
  useSidekickFeatureEventReporter,
} from '../../featureEvents';
import { resetChat, selectMessages } from '../../state/aiSidekickChatSlice';
import {
  beginResetConfirm,
  closeResetConfirm,
  resetUiState,
  selectIsResetConfirmLoading,
} from '../../state/aiSidekickSlice';
import type { StateWithSidekickChat } from '../../state/chatTypes';
import { SIDEKICK_TRANSLATION_NAMESPACE } from '../../translations';
import { useSidekickPanelContext } from '../AISidekickPanel/SidekickPanelContext';
import InlineMessageCard from '../InlineMessageCard';
import { MOCK_RESET_DELAY_MS } from './constants';

/**
 * In-chat "Start a new chat?" confirmation prompt. Renders the generic
 * `InlineMessageCard` primitive with reset-specific copy and dispatches
 * the package-owned reset (`resetChat()` + `resetUiState()`) on Continue.
 *
 * Loading is a Redux flag (`selectIsResetConfirmLoading`) flipped by
 * `beginResetConfirm()`; the component owns only the mock-delay timer
 * (`timerRef`), not the loading state. `resetUiState()` is the single
 * source of truth for clearing both `isResetConfirmOpen` and
 * `isResetConfirmLoading` (see slice), so the prompt does NOT also
 * dispatch `closeResetConfirm()` — that would be a no-op duplicate
 * that drifts the moment one of the two reducers changes. The
 * `onNewConversation` callback is read from `SidekickPanelContext`
 * and fired AFTER the store is reset so consumers observe a clean
 * slate (analytics, tracing, app cleanup).
 *
 * Visibility is owned by the parent (`ChatInterface` reads
 * `selectIsResetConfirmOpen` and renders this conditionally), so the
 * prompt itself never reads the open flag — it just renders. The
 * prompt is dismissed via Cancel / X by dispatching
 * `closeResetConfirm()`; the auto-dismiss-on-typing behavior is wired
 * one level up in `ChatInterface` because it observes the input bar.
 */
function ResetConversationPrompt() {
  const { t } = useTranslation(SIDEKICK_TRANSLATION_NAMESPACE);
  const dispatch = useDispatch();
  const store = useStore<StateWithSidekickChat>();
  const { captureEmit } = useSidekickFeatureEventReporter();
  const { onNewConversation } = useSidekickPanelContext();
  const isLoading = useSelector(selectIsResetConfirmLoading);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const promptRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    promptRef.current?.focus();
  }, []);

  useEffect(() => {
    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
        // Host-driven unmount during the in-flight loading window
        // (route navigation, feature-flag flip, panel re-key) cancels
        // the pending dispatch but would otherwise leave the
        // Redux-owned prompt flags (`isResetConfirmOpen` +
        // `isResetConfirmLoading`) set forever. On remount, the prompt
        // would re-render in a soft-deadlock: primary, Cancel, X, and
        // the header edit button are all gated against
        // `isResetConfirmLoading`, leaving the user no in-app escape
        // path. Dispatching `resetUiState()` clears both flags so the
        // semantic is "navigating away during a pending reset = cancel"
        // — chat messages survive, the UI lands clean. The same
        // pattern carries over verbatim when the mock setTimeout is
        // replaced with the API-backed mutation; the cleanup will
        // still need to clear the loading flag if the request is
        // aborted on unmount.
        dispatch(resetUiState());
      }
    };
  }, [dispatch]);

  const handleCancel = useCallback(() => {
    dispatch(closeResetConfirm());
  }, [dispatch]);

  const handleContinue = useCallback(() => {
    // Synchronous double-click guard. The button's loading-disabled
    // state comes from a Redux flag that only flips on the next React
    // render, so two clicks queued inside the same event-loop tick
    // would both pass the disabled gate, share the same closure, and
    // each schedule a setTimeout — firing onNewConversation twice and
    // double-dispatching the reset. The ref is mutated synchronously
    // when the timer is scheduled, so the second call sees the live
    // pending timer and short-circuits.
    if (timerRef.current !== null) {
      return;
    }
    dispatch(beginResetConfirm());
    const emitReset = captureEmit();
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      emitReset({
        type: SIDEKICK_FEATURE_EVENT.CONVERSATION_RESET,
        messageCount: selectMessages(store.getState()).length,
      });
      dispatch(resetChat());
      // resetUiState clears isResetConfirmOpen + isResetConfirmLoading
      // (single source of truth for prompt-flag teardown, see slice),
      // so an additional closeResetConfirm dispatch would be a dead
      // duplicate that drifts the moment one reducer changes.
      dispatch(resetUiState());
      onNewConversation?.();
    }, MOCK_RESET_DELAY_MS);
  }, [captureEmit, dispatch, onNewConversation, store]);

  return (
    <InlineMessageCard
      ref={promptRef}
      tabIndex={-1}
      title={t('chat.reset.title')}
      body={t('chat.reset.body')}
      onDismiss={handleCancel}
      closeAriaLabel={t('chat.reset.closeAriaLabel')}
      data-testid="ai-sidekick-reset-prompt"
      secondaryAction={{
        label: t('chat.reset.cancelLabel'),
        onClick: handleCancel,
        'data-testid': 'ai-sidekick-reset-prompt-cancel',
      }}
      primaryAction={{
        label: t('chat.reset.continueLabel'),
        onClick: handleContinue,
        isLoading,
        loadingAriaLabel: t('chat.reset.loadingAriaLabel'),
        'data-testid': 'ai-sidekick-reset-prompt-continue',
      }}
    />
  );
}

export default ResetConversationPrompt;
